import React, { FC, useState, useEffect, useCallback, memo } from 'react';
import { IdText, ResponseLevel } from '../MaintenanceTriggerForm.slice';
import { AnnotationStandardDetails, FindingTypeDetails } from '../../../AnnotationManager/AnnotationStandardForm/AnnotationStandardForm.slice';
import Cell from './Cell';
import {
  getSelectedFindingTypeAndPopulateRule,
  PropConstants,
  getFindingTypeAndColumns,
  tresholdColumns,
  updateRuleWithClearFieldsWhereEmpty
} from '../Helpers';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faEdit } from '@fortawesome/free-solid-svg-icons';
import styles from '../MaintenanceTriggerForm.module.scss';
import { FormDataInitial } from '../formSelector';
import classNames from 'classnames';

type Props = {
  rule: any;
  standard: AnnotationStandardDetails;
  columns: IdText[];
  responseLevels: ResponseLevel[];
  formData: FormDataInitial;
  onChange: (updatedRule: any) => void;
  onFindingTypeSelected: (properties: IdText[]) => void;
  onDeleteRow: (index: number) => void;
};

const Row: FC<Props> = memo(({ rule, standard, columns, responseLevels, formData, onChange, onFindingTypeSelected, onDeleteRow }) => {
  const [findingTypes, setFindingTypes] = useState<IdText[]>([]);
  const [findingType, setFindingType] = useState<FindingTypeDetails | null>(null);
  const [updated, setUpdated] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const findingTypeUpdated = useCallback(
    (key: string, value: string, updatedRule: any) => {
      const { selectedFindingType, findingTypeColumns } = getSelectedFindingTypeAndPopulateRule(standard, updatedRule, key, value);

      setFindingType(selectedFindingType);
      onFindingTypeSelected(findingTypeColumns);
    },
    [onFindingTypeSelected, standard]
  );

  const onCellUpdated = useCallback(
    (key: string | number, value: string) => {
      const updatedRule = { ...rule };

      if (standard && key === PropConstants.FINDING_TYPE) {
        findingTypeUpdated(key, value, updatedRule);
      }

      if (standard && key === PropConstants.RESPONSE_LEVEL_ACTION) {
        const responseLevel = responseLevels.find(x => x.id === value)!;
        updatedRule[PropConstants.RESPONSE_LEVEL_DESCRIPTION] = responseLevel.description;
      }

      updatedRule[key] = value;

      onChange(updatedRule);
    },
    [findingTypeUpdated, onChange, responseLevels, rule, standard]
  );

  useEffect(() => {
    if (!rule[PropConstants.ID]) {
      setEditMode(true);
    }
  }, [rule]);

  useEffect(() => {
    if (!standard) return;

    const standardFindingTypes = standard.findingTypes.map(x => ({
      id: x.id!,
      text: x.findingTypeTemplate?.name!
    }));

    setFindingTypes(standardFindingTypes);
  }, [standard]);

  useEffect(() => {
    if (!standard) return;

    if (rule[PropConstants.FINDING_TYPE] && !findingType) {
      const { selectedFindingType } = getFindingTypeAndColumns(standard, rule[PropConstants.FINDING_TYPE]);
      setFindingType(selectedFindingType);
    }
  }, [findingType, rule, standard]);

  useEffect(() => {
    if (formData.isUpdate && !updated && rule.id) {
      const updatedRule = { ...rule };
      const { selectedFindingType, findingTypeColumns } = getFindingTypeAndColumns(standard, rule[PropConstants.FINDING_TYPE]);
      updateRuleWithClearFieldsWhereEmpty(findingTypeColumns, updatedRule);
      setFindingType(selectedFindingType);
      onFindingTypeSelected(findingTypeColumns);
      tresholdColumns.forEach(column => {
        if (updatedRule[column.id] === undefined) {
          updatedRule[column.id] = '';
        }
      });
      onChange(updatedRule);
      setUpdated(true);
    }
  }, [formData.isUpdate, onChange, onFindingTypeSelected, rule, standard, updated]);

  return (
    <motion.tr initial={{ opacity: 0, x: -100 }} animate={{ opacity: 1, x: 0 }} key={rule.index}>
      <td key='actions' className={styles.actionsColumn}>
        <FontAwesomeIcon
          icon={faEdit}
          onClick={e => setEditMode(!editMode)}
          className={classNames(styles.rowActions, { [styles.noAction]: !rule[PropConstants.ID] })}
        />
        <FontAwesomeIcon icon={faTimes} onClick={e => onDeleteRow(rule.index)} className={styles.rowActions} />
      </td>
      {columns.map(column => (
        <Cell
          editMode={editMode}
          key={column.id}
          column={column}
          findingType={findingType}
          findingTypes={findingTypes}
          rule={rule}
          onCellUpdated={onCellUpdated}
          responseLevels={responseLevels}
          formData={formData}
        />
      ))}
    </motion.tr>
  );
});

export default Row;
